import { ISaSmsTenantRow } from '@interfaces/sa-sms';
import { Space, Table, Tag, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NumberBillingTag, OnOffTag, RegistrationTag } from './sms-status-tags';

type Props = {
    rows: ISaSmsTenantRow[];
    loading: boolean;
    showVoice: boolean;
    onOpen: (tenantId: string) => void;
};

const pct = (v: number | null | undefined) => (v === null || v === undefined ? '-' : `${v}%`);

/** Cross-tenant table of the console: sender / A2P / tier / toggles / flags / balance / 7-day delivery / number billing. */
export const SmsTenantTable: React.FC<Props> = ({ rows, loading, showVoice, onOpen }) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const num = (n: number) => n.toLocaleString(i18n.language);
    return (
        <Table<ISaSmsTenantRow>
            rowKey="tenantId"
            loading={loading}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 1400 }}
            size="middle"
        >
            <Table.Column<ISaSmsTenantRow>
                title={t('list.tenant')}
                fixed="left"
                render={(_, r) => <a onClick={() => onOpen(r.tenantId)}>{r.name}</a>}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.number')}
                render={(_, r) => (
                    <Space direction="vertical" size={0}>
                        <span>{r.sender?.phoneMasked ?? '-'}</span>
                        <RegistrationTag status={r.sender?.registrationStatus} />
                    </Space>
                )}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.subaccount')}
                render={(_, r) => (
                    <Typography.Text code>{r.sender?.subaccountSid ?? '-'}</Typography.Text>
                )}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.tier')}
                render={(_, r) => <Tag>{t(`tier.${r.riskTier}`)}</Tag>}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.marketing')}
                align="center"
                render={(_, r) => <OnOffTag on={r.marketingEnabled} />}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.twoWay')}
                align="center"
                render={(_, r) => <OnOffTag on={r.twoWayEnabled} />}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.paused')}
                align="center"
                render={(_, r) =>
                    r.outboundPaused ? (
                        <Tooltip title={r.pausedReason ?? undefined}>
                            <Tag color="error">{t('common.paused')}</Tag>
                        </Tooltip>
                    ) : (
                        '-'
                    )
                }
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.flags')}
                render={(_, r) =>
                    r.flags.length ? (
                        <Tooltip
                            title={r.flags
                                .map((f) => t(`flags.${f}`, { defaultValue: f }))
                                .join(', ')}
                        >
                            <Tag color="warning">{r.flags.length}</Tag>
                        </Tooltip>
                    ) : (
                        '-'
                    )
                }
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.balance')}
                align="right"
                render={(_, r) =>
                    r.balance ? (
                        <Tooltip
                            title={t('list.balanceSplit', {
                                included: num(r.balance.included),
                                purchased: num(r.balance.purchased),
                            })}
                        >
                            {num(r.balance.available)}
                        </Tooltip>
                    ) : (
                        '-'
                    )
                }
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.sent7d')}
                align="right"
                render={(_, r) => (r.week ? num(r.week.sent) : '-')}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.delivery')}
                align="right"
                render={(_, r) => pct(r.week?.deliveryPct)}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.failure')}
                align="right"
                render={(_, r) => pct(r.week?.failurePct)}
            />
            <Table.Column<ISaSmsTenantRow>
                title={t('list.numberBilling')}
                render={(_, r) => <NumberBillingTag status={r.numberBilling} />}
            />
            {showVoice && (
                <Table.Column<ISaSmsTenantRow>
                    title={t('list.voice7d')}
                    align="right"
                    render={(_, r) => (r.voiceMinutes7d === null ? '-' : num(r.voiceMinutes7d))}
                />
            )}
        </Table>
    );
};
